import { AxiosError } from 'axios';

import { ExperimentId } from '../../common/experiments/experimentsConstants';
import { ApplicationType } from '../../common/platform/platformTypes';
import { isFeatureEnabled } from '../../common/users/utils/userPropertyUtils';
import { METHODS } from '../../common/utils/http/httpConstants';
import platformSingleton from '../platform/platformSingleton';
import {
    isPlatformElectronMac,
    isPlatformModernMobileApp,
    isPlatformPwa,
} from '../platform/utils/platformDetailsUtils';
import {
    removeCurrentUserToken,
    getCurrentUserToken,
    setCurrentUserToken,
    removeRememberedBetterAuthSessionUser,
    setRememberedBetterAuthSessionUser,
    type BetterAuthSessionUser,
} from './authService';
import { http } from '../utils/services/http';

export type BetterAuthSessionMigrationStatus =
    | 'idle'
    | 'upgrading'
    | 'downgrading'
    | 'upgraded'
    | 'downgraded'
    | 'failed';

let migrationStatus: BetterAuthSessionMigrationStatus = 'idle';

export const getBetterAuthSessionMigrationStatus = (): BetterAuthSessionMigrationStatus => migrationStatus;

export const resetBetterAuthSessionMigrationStatus = (): void => {
    migrationStatus = 'idle';
};

// Installed PWAs need no cookie relaxation at all: a PWA is served first-party from the app host,
// so its session cookie is the same `SameSite=Lax; Secure` host-only cookie the browser already
// stores for web — none of the origin override branches in authRoutes apply. All six PWA app types
// qualify, which notably covers every Windows desktop user, as there is no Windows Electron build.
//
// Native (Capacitor) migration depends on the WebView storing the Secure session cookie, which it
// only does when the API host is same-site with the WebView origin. Every environment a native build
// may point at now satisfies that: deployed builds talk to a milanote.com host, Android WebView
// applies no third-party cookie policy at all, and the iOS dev builds either serve the WebView from
// the dev API host itself or point at an https milanote.com host (see
// docs/run-capacitor-mobile-apps.md).
//
// Electron loads the renderer from file:// (an opaque origin), so every API request is cross-site,
// and the server issues the session cookie as `SameSite=None; Secure` for the Electron origin.
// Chromium applies no third-party cookie blocking in Electron, so the cookie is stored and re-sent
// from the network stack without any renderer involvement (see docs/run-electron-app.md).
//
// Four ApplicationType values are deliberately left out.
//
// `iframe` only ever renders a public board: framing is denied globally by a `frame-ancestors:
// 'none'` CSP and lifted only for public boards (see boardRouteMiddleware), and the login and
// register modals render nothing when framed. A framed client therefore has no session to migrate
// in the first place. The cookie mechanics point the same way — a `SameSite=Lax` cookie is never
// sent to a third-party frame — but the absence of a session is the reason.
//
// `iphoneSwift`, `ipadSwift` and `androidFlutter` are the pre-Capacitor native apps. We no longer
// ship updates for them, so they stay on legacy auth rather than gaining a migration path.
const isBetterAuthSessionMigrationPlatform = (): boolean =>
    platformSingleton.appType === ApplicationType.web ||
    isPlatformPwa(platformSingleton) ||
    isPlatformModernMobileApp(platformSingleton) ||
    isPlatformElectronMac(platformSingleton);

export const shouldUpgradeLegacySessionToBetterAuth = (user: object | null | undefined): boolean => {
    if (!user) return false;
    if (!isBetterAuthSessionMigrationPlatform()) return false;
    if (!getCurrentUserToken()) return false;
    if (!isFeatureEnabled(ExperimentId.betterAuth, user)) return false;

    return migrationStatus === 'idle';
};

export const shouldDowngradeBetterAuthSessionToLegacy = (user: object | null | undefined): boolean => {
    if (!user) return false;
    if (!isBetterAuthSessionMigrationPlatform()) return false;
    if (getCurrentUserToken()) return false;
    if (isFeatureEnabled(ExperimentId.betterAuth, user)) return false;

    return migrationStatus === 'idle';
};

const verifyBetterAuthSession = async (): Promise<BetterAuthSessionUser | null> => {
    const response = await http({
        url: 'users/me',
        withCredentials: true,
        skipAuthHeader: true,
        retry: 0,
    });

    const user = response.data?.user;
    if (!user) return null;
    if (typeof user !== 'object') throw new Error('Verified Better Auth session returned invalid user data');

    return user as BetterAuthSessionUser;
};

export const upgradeLegacySessionToBetterAuthIfFlagged = async (user: object | null | undefined): Promise<boolean> => {
    if (!shouldUpgradeLegacySessionToBetterAuth(user)) return false;

    migrationStatus = 'upgrading';

    try {
        await http({
            method: METHODS.POST,
            url: 'auth/upgrade-better-auth-session',
            withCredentials: true,
        });

        // The upgrade response sets the session cookie via `Set-Cookie`, which the WebView stores;
        // verify the cookie-only /users/me request authenticates before dropping the legacy token.
        const verifiedUser = await verifyBetterAuthSession();
        setRememberedBetterAuthSessionUser(verifiedUser || user);
        removeCurrentUserToken();

        migrationStatus = 'upgraded';
        return true;
    } catch (error) {
        migrationStatus = 'failed';
        throw error as AxiosError;
    }
};

export const downgradeBetterAuthSessionToLegacyIfUnflagged = async (
    user: object | null | undefined,
): Promise<boolean> => {
    if (!shouldDowngradeBetterAuthSessionToLegacy(user)) return false;

    migrationStatus = 'downgrading';

    try {
        const response = await http({
            method: METHODS.POST,
            url: 'auth/downgrade-better-auth-session',
            withCredentials: true,
            skipAuthHeader: true,
        });

        const token = response.data?.token;
        if (typeof token !== 'string') throw new Error('Downgraded Better Auth session did not return a legacy token');

        setCurrentUserToken(token);
        removeRememberedBetterAuthSessionUser();

        migrationStatus = 'downgraded';
        return true;
    } catch (error) {
        migrationStatus = 'failed';
        throw error as AxiosError;
    }
};
